import axios from 'axios';
import { useContext, useState, useEffect } from 'react';

import { ModalContext } from 'app/app';
import DateHelper from 'helpers/dateUtils';
import { FieldInput } from 'components/fieldInput';
import { Pagination } from 'components/Pagination';
import { IconedButton } from 'components/IconedButton';
import InputUtils from 'helpers/inputUtils';
import {
  Animate,
  Grid,
  Search,
  Table,
  useAPI,
  useNavigate,
  ViewContext,
} from 'components/lib';

import styles from './styles.module.scss';
import debounce from 'helpers/debounce';

export function Renewals() {
  const navigate = useNavigate();
  const user = useAPI('/api/user');
  const context = useContext(ViewContext);
  const { setModal } = useContext(ModalContext);
  const dateFormatter = new DateHelper();
  const inputUtils = new InputUtils();
  const [payload, setPayload] = useState();
  const [renewals, setRenewals] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [searchQueryString, setSearchQueryString] = useState('');
  const [statusFilter, setStatusFilter] = useState('Active');
  const [ticketStatusFilter, setTicketStatusFilter] = useState([]);
  const [agencyId, setAgencyId] = useState();

  async function fetchRenewals(page, search, status) {
    setLoading(true);

    await axios
      .get(`/api/renewals/${agencyId}`, {
        params: {
          page,
          search,
          status,
          sortBy: 'createdAt',
          sortOrder: 'desc',
        },
      })
      .then((response) => {
        if (response.data && response.data.success) {
          setPayload(response.data);
          const formattedRenewals = response.data.data.map(
            (renewal) => ({
              id: renewal.id,
              Number: renewal.renewalNumber,
              'Agency name': renewal.agencyName
                ? renewal.agencyName
                : 'N/A',
              'Company Name':
                renewal.companyName &&
                inputUtils
                  .capitalizeFirstLetter(renewal.companyName)
                  .replaceAll('Llc', 'LLC'),
              'Submission Number': renewal.submissionNumber
                ? renewal.submissionNumber
                : renewal.id,
              'Created At':
                renewal.createdAt &&
                dateFormatter.timestamp(renewal.createdAt),
              'Updated At':
                renewal.updatedAt &&
                dateFormatter.timestamp(renewal.updatedAt),
              status: renewal.status,
            })
          );
          setRenewals(formattedRenewals);
        } else {
          context.notification.show(
            'Unexpected API response structure',
            'error',
            true,
            'toast',
            'x-circle'
          );
        }
      })
      .catch(() => {
        context.notification.show(
          'Failed to load renewals',
          'error',
          true,
          'toast',
          'x-circle'
        );
      })
      .finally(() => {
        setLoading(false);
      });
  }

  useEffect(() => {
    if (user?.data?.account_id) {
      setAgencyId(user?.data?.account_id);
    }
  }, [user]);

  useEffect(() => {
    if (agencyId) {
      fetchRenewals(
        page,
        searchQueryString,
        statusFilter,
        ticketStatusFilter
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    page,
    searchQueryString,
    statusFilter,
    ticketStatusFilter,
    agencyId,
  ]);

  const handleStatusFilter = (status) => {
    setStatusFilter(status);
    setPage(1);
  };

  const debouncedSetSearchQueryString = debounce((query) => {
    setSearchQueryString(query);
    setPage(1);
  }, 500);

  return (
    <Animate type="pop">
      <div
        style={{
          display: 'flex',
          alignContent: 'center',
          justifyContent: 'space-between',
        }}
      >
        <div style={{ display: 'flex' }}>
          {['Active', 'Bound', 'Declined', 'Expired'].map(
            (status) => {
              return (
                <div
                  key={status}
                  style={{
                    marginLeft: '5px',
                    width: '150px',
                    backgroundColor:
                      statusFilter === status ? 'black' : '#EAEAEA',
                    color:
                      statusFilter === status ? 'white' : '#090E3480',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: 10,
                    fontSize: 16,
                    fontWeight: 600,
                    borderTopLeftRadius: 10,
                    borderTopRightRadius: 10,
                    cursor: 'pointer',
                  }}
                  onClick={() => handleStatusFilter(status)}
                >
                  {status}
                </div>
              );
            }
          )}
        </div>
      </div>
      <Grid cols="1">
        <div className={styles.wrapper} style={{ marginTop: 0 }}>
          <div
            style={{
              marginBottom: 10,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <div style={{ width: '80%', marginTop: 30 }}>
              <Search
                callback={(query) => {
                  debouncedSetSearchQueryString(query);
                }}
              />
            </div>
            <div style={{ width: '20%', marginLeft: 20 }}>
              <FieldInput
                id="ticketStatus_filter"
                type="multi_select"
                label="Filters"
                defaultValue={[]}
                options={[
                  { value: 'Pending', label: 'Pending' },
                  { value: 'In Progress', label: 'In Progress' },
                ]}
                handleChange={(ticketStatus) => {
                  setTicketStatusFilter(
                    ticketStatus.map((status) => {
                      return status.value;
                    })
                  );
                }}
              />
            </div>
          </div>
          <Table
            data={renewals}
            loading={loading}
            hide={['id', 'assignedUserEmail', 'userId', 'status']}
            actions={{
              custom: [
                {
                  icon: 'search',
                  color: '#637381',
                  action: (row) => {
                    navigate(`/app/renewals/${row.id}`);
                  },
                },
              ],
            }}
          />
          {payload && !loading && (
            <Pagination
              pagesCount={payload.pages}
              active={payload.page}
              onChange={(newPage) => {
                setPage(newPage);
              }}
            />
          )}
        </div>
      </Grid>
    </Animate>
  );
}
